import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  type Auth,
  type User,
  type Unsubscribe,
} from 'firebase/auth';
import {
  getMessaging,
  isSupported as isMessagingSupported,
  type Messaging,
} from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  // messagingSenderId + appId are required by FCM `getToken` (Firebase
  // Installations + the FCM sender). Absent in auth-only envs; harmless there.
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// How long to wait for the first onAuthStateChanged before unblocking interceptors.
// Guards against Firebase SDK hangs on network failure or SDK CDN block.
// Exported so the apiClient response interceptor can derive its 401 grace
// window from this value (the grace must outlast the gate timeout).
export const FIREBASE_AUTH_READY_TIMEOUT_MS = 3000;

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let messaging: Messaging | null = null;

let firebaseAuthReadyPromise: Promise<void> | null = null;

/**
 * Lazily create the single shared Firebase app. Both auth and messaging use
 * this one instance. Returns null when Firebase isn't configured (dev without
 * env vars) so callers can no-op gracefully.
 */
function getFirebaseApp(): FirebaseApp | null {
  if (app) return app;

  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    return null;
  }

  app = initializeApp(firebaseConfig);
  return app;
}

/** Initialize Firebase only in production (or when config is available) */
function getFirebaseAuth(): Auth | null {
  if (auth) return auth;

  const firebaseApp = getFirebaseApp();
  if (!firebaseApp) return null;

  auth = getAuth(firebaseApp);
  return auth;
}

/**
 * The Firebase Messaging instance for browser web-push, or null when Firebase
 * isn't configured or the browser doesn't support the Messaging APIs. Lazy and
 * side-effect-free until called — never requests notification permission here.
 *
 * `isMessagingSupported()` is async, so this returns a Promise.
 */
export async function getFirebaseMessaging(): Promise<Messaging | null> {
  if (messaging) return messaging;

  // Deliberately `isFirebaseAppConfigured()`, NOT `isFirebaseConfigured()`:
  // messaging depends on the Firebase APP, and is independent of which auth
  // path the session took. Gating it on the auth predicate made push silently
  // dead under `VITE_FORCE_TEST_USER=1`.
  if (!isFirebaseAppConfigured()) return null;

  const firebaseApp = getFirebaseApp();
  if (!firebaseApp) return null;

  if (!(await isMessagingSupported())) return null;

  messaging = getMessaging(firebaseApp);
  return messaging;
}

export async function signInWithEmail(
  email: string,
  password: string,
): Promise<User> {
  const firebaseAuth = getFirebaseAuth();
  if (!firebaseAuth) {
    throw new Error('Firebase not configured');
  }
  const credential = await signInWithEmailAndPassword(
    firebaseAuth,
    email,
    password,
  );
  return credential.user;
}

export async function signInWithGoogle(): Promise<User> {
  const firebaseAuth = getFirebaseAuth();
  if (!firebaseAuth) {
    throw new Error('Firebase not configured');
  }
  const provider = new GoogleAuthProvider();
  const credential = await signInWithPopup(firebaseAuth, provider);
  return credential.user;
}

export async function signOut(): Promise<void> {
  const firebaseAuth = getFirebaseAuth();
  if (firebaseAuth) {
    await firebaseSignOut(firebaseAuth);
  }
}

export function onAuthStateChanged(
  callback: (user: User | null) => void,
): Unsubscribe {
  const firebaseAuth = getFirebaseAuth();
  if (!firebaseAuth) {
    // Not configured - call with null immediately
    callback(null);
    return () => {};
  }
  return firebaseOnAuthStateChanged(firebaseAuth, callback);
}

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const firebaseAuth = getFirebaseAuth();
  if (!firebaseAuth?.currentUser) return null;
  return firebaseAuth.currentUser.getIdToken(forceRefresh);
}

export async function resetPassword(email: string): Promise<void> {
  const firebaseAuth = getFirebaseAuth();
  if (!firebaseAuth) {
    throw new Error('Firebase not configured');
  }
  await sendPasswordResetEmail(firebaseAuth, email);
}

export async function verifyResetCode(oobCode: string): Promise<string> {
  const firebaseAuth = getFirebaseAuth();
  if (!firebaseAuth) throw new Error('Firebase not configured');
  return verifyPasswordResetCode(firebaseAuth, oobCode);
}

export async function confirmReset(
  oobCode: string,
  newPassword: string,
): Promise<void> {
  const firebaseAuth = getFirebaseAuth();
  if (!firebaseAuth) throw new Error('Firebase not configured');
  await confirmPasswordReset(firebaseAuth, oobCode, newPassword);
}

export function waitForFirebaseAuthReady(): Promise<void> {
  const firebaseAuth = getFirebaseAuth();
  if (!firebaseAuth) {
    // Firebase not configured (dev mode, no env vars) — nothing to wait for.
    return Promise.resolve();
  }

  if (firebaseAuthReadyPromise) {
    return firebaseAuthReadyPromise;
  }

  firebaseAuthReadyPromise = new Promise<void>((resolve) => {
    // Resolve on first auth state callback (user or null — either means Firebase
    // has finished reading from IndexedDB/localStorage). Fallback timeout
    // unblocks if Firebase is unreachable (network failure, SDK CDN block).
    let unsubscribe = () => {};
    const timer = setTimeout(() => {
      unsubscribe();
      resolve();
    }, FIREBASE_AUTH_READY_TIMEOUT_MS);
    unsubscribe = firebaseOnAuthStateChanged(firebaseAuth, () => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    });
  });

  return firebaseAuthReadyPromise;
}

/**
 * Whether the Firebase **app** is configured — i.e. `initializeApp` would
 * succeed. This is the raw config check, with NO auth-path short-circuit.
 *
 * Use this for anything that needs the Firebase app itself (messaging/FCM).
 * Use {@link isFirebaseConfigured} only to decide whether Firebase *auth* is
 * the active auth path.
 */
export function isFirebaseAppConfigured(): boolean {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId);
}

/**
 * Whether Firebase **auth** is the active auth path.
 *
 * Despite the general-sounding name (kept because ~20 call sites read it as
 * "are we doing Firebase auth?"), this is an AUTH predicate: it is false
 * whenever the dev test-user bypass is forced, even though the Firebase app
 * is perfectly well configured. Anything that is not an auth decision wants
 * {@link isFirebaseAppConfigured} instead.
 */
export function isFirebaseConfigured(): boolean {
  // VITE_FORCE_TEST_USER=1 in dev forces the test-user-id auth path even when
  // Firebase env vars are present — useful for screenshot/scripted runs that
  // shouldn't go through the Google login flow. Has no effect in production.
  if (import.meta.env.DEV && import.meta.env.VITE_FORCE_TEST_USER === '1') {
    return false;
  }
  return isFirebaseAppConfigured();
}
